import * as JSZip from 'jszip';
import notificationActions from '../state/notification/notificationActions';
import ModuleOutputV2 from '@biolibtech/biolib-js/src/AppClient/biolibBinaryFormat/ModuleOutputV2';
import { ApiTagTypes, baseApi, IPaginatedResponse, rewriteUrlIfDev } from './baseApi';
import { queryWithRetries, uploadModuleInput } from './jobsApiUtils';
import { saveFileAs } from '@utils';
import { IJob, IJobPost } from '@biolibtech/biolib-js';
import { IGetAppResponse } from '@biolibtech/biolib-js/src/BioLib/resources/App';
import config from '../config';
import axios from 'axios';
import ModuleInput, { IModuleInput } from '@biolibtech/biolib-js/src/AppClient/biolibBinaryFormat/ModuleInput';

interface IResult {
    created_at: string;
    name: string;
    ready_at: string;
    state: 'pending' | 'ready';
    uuid: string;
}

export interface IJobSlim {
    app_uri: string;
    auth_token: string;
    ended_at: string;
    exit_code: number | null;
    main_result: null | IResult;
    started_at: string;
    state: string;
    uuid: string;
}

interface IJobMetric {
    app_uri: string;
    client_opt_user_info: string | null;
    client_type: string;
    created_at: string;
    exit_code: number | null;
    state: string;
    user: { email: string } | null;
    uuid: string;
}

export interface ICreateJobArgs {
    appUri?: string;
    appVersionUuid?: string;
    appResourceNamePrefix?: string;
    temporaryClientSecrets?: Record<string, string>;
    experimentUuid?: string;
    machine?: string;
}

export interface IStartJobArgs {
    job: IJob;
    moduleInput: IModuleInput;
    resultNamePrefix?: string;
}

const INLINE_UPLOAD_MAX_INPUT_SIZE_IN_BYTES = 500_000;

const jobsApi = baseApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
        getJob: builder.query<IJob, { jobUuid: string }>({
            query: (params) => ({ url: `/jobs/${params.jobUuid}/` }),
        }),
        listJobs: builder.query<
            IPaginatedResponse<IJobSlim>,
            {
                page: number;
                page_size?: number;
                search?: string;
                ordering?: string;
                state?: string;
                created_before?: string;
            }
        >({
            query: (params) => ({ url: '/jobs/', params }),
            providesTags: (data, error, args) =>
                !data
                    ? [{ type: ApiTagTypes.job, id: 'PARTIAL-LIST' }]
                    : [
                          ...data.results.map((job) => ({ type: ApiTagTypes.job, id: job.uuid })),
                          { type: ApiTagTypes.job, id: 'PARTIAL-LIST' },
                      ],
        }),
        getAccountJobMetrics: builder.query<IPaginatedResponse<IJobMetric>, { accountUuid: string; page: number }>({
            query: (args) => ({
                url: `/accounts/${args.accountUuid}/metrics/jobs/`,
                params: { page: args.page, exclude_sub_jobs: 'true' },
            }),
            providesTags: [ApiTagTypes.accountJobMetrics],
        }),
        createJob: builder.mutation<IJob, ICreateJobArgs>({
            queryFn: async (args, _api, _extraOptions, baseQuery) => {
                if (args.appUri && args.appVersionUuid) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            error: 'createJob accepts either appUri or appVersionUuid, not both',
                        },
                    };
                }

                let appVersionUuid = args.appVersionUuid;
                let appResourceNamePrefix = args.appResourceNamePrefix;

                if (args.appUri) {
                    const appResponse = await baseQuery({ url: '/app/', params: { uri: args.appUri } });
                    if (appResponse.error) {
                        return { error: appResponse.error };
                    }
                    const { app, app_version } = appResponse.data as IGetAppResponse;
                    appVersionUuid = app_version.public_id;
                    appResourceNamePrefix = app.resource_name_prefix;
                }

                const jobResponse = await baseQuery({
                    url: '/jobs/',
                    method: 'POST',
                    body: {
                        app_version_id: appVersionUuid,
                        client_type: 'biolib-frontend',
                        client_version: config.frontendVersion,
                        ...(appResourceNamePrefix ? { app_resource_name_prefix: appResourceNamePrefix } : {}),
                        ...(args.temporaryClientSecrets ? { temporary_client_secrets: args.temporaryClientSecrets } : {}),
                        ...(args.experimentUuid ? { experiment_uuid: args.experimentUuid } : {}),
                        ...(args.machine ? { requested_machine: args.machine } : {}),
                    } as IJobPost,
                });
                if (jobResponse.error) {
                    return { error: jobResponse.error };
                }
                return { data: jobResponse.data as IJob };
            },
            invalidatesTags: [
                { type: ApiTagTypes.job, id: 'PARTIAL-LIST' },
            ],
        }),
        startJob: builder.mutation<null, IStartJobArgs>({
            invalidatesTags: (result, error, args) => [
                { type: ApiTagTypes.job, id: args.job.public_id },
                { type: ApiTagTypes.job, id: 'PARTIAL-LIST' },
            ],
            queryFn: async (args, _api, _extraOptions, baseQuery) => {
                const { job, moduleInput, resultNamePrefix } = args;
                const inputSerialized = new ModuleInput(moduleInput).serialize();

                if (inputSerialized.byteLength <= INLINE_UPLOAD_MAX_INPUT_SIZE_IN_BYTES) {
                    const jobResponse = await queryWithRetries(baseQuery, {
                        url: '/jobs/create_job_with_data/',
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/octet-stream',
                            'existing-job-uuid': job.public_id,
                            'existing-job-auth-token': job.auth_token,
                            ...(resultNamePrefix ? { 'result-name-prefix': resultNamePrefix } : {}),
                        },
                        body: inputSerialized,
                    });
                    if (jobResponse.error) {
                        return { error: jobResponse.error };
                    }
                } else {
                    const uploadError = await uploadModuleInput(job, inputSerialized, baseQuery);
                    if (uploadError) {
                        return { error: uploadError };
                    }
                    const cloudJobResponse = await queryWithRetries(baseQuery, {
                        url: '/jobs/cloud/',
                        method: 'POST',
                        body: { job_id: job.public_id, module_name: 'main', result_name_prefix: resultNamePrefix },
                    });
                    if (cloudJobResponse.error) {
                        return { error: cloudJobResponse.error };
                    }
                }

                return { data: null };
            },
        }),
        downloadJobResult: builder.mutation<null, { jobUuid: string; jobAuthToken?: string }>({
            queryFn: async (args, { signal, dispatch, getState }, extraOptions, baseQuery) => {
                const { jobUuid, jobAuthToken } = args;

                interface IResultData {
                    job: IJobSlim;
                    presigned_download_url: string;
                    presigned_input_download_url: string;
                }

                const { data, error } = await baseQuery({
                    headers: { 'Job-Auth-Token': jobAuthToken },
                    url: `/jobs/${jobUuid}/storage/results/download/`,
                });
                if (error) {
                    dispatch(notificationActions.addError(`Failed to get download URL: ${JSON.stringify(error.data)}`));
                    return { error };
                }

                const { job, presigned_download_url, presigned_input_download_url } = data as IResultData;

                let moduleInput: ModuleInput | null = null;
                try {
                    const { data } = await axios.get<ArrayBuffer>(rewriteUrlIfDev(presigned_input_download_url), {
                        responseType: 'arraybuffer',
                    });
                    moduleInput = new ModuleInput(new Uint8Array(data));
                } catch (error) {
                    console.error(error);
                    const message = `Failed to include input in result download got error: ${error}`;
                    dispatch(notificationActions.addError(message));
                }

                try {
                    const moduleOutput = new ModuleOutputV2(rewriteUrlIfDev(presigned_download_url));
                    const sizeInBytes = await moduleOutput.getSizeInBytes();
                    if (sizeInBytes > 1_000_000_000) {
                        throw new Error('Result is to large to download from the browser.');
                    }
                    await moduleOutput.fetchAllData();
                    const exit_code = await moduleOutput.getExitCode();
                    const stdout = await moduleOutput.getStdout();
                    const stderr = await moduleOutput.getStderr();
                    const outputFiles = await moduleOutput.getFiles();

                    let metadata: Record<string, any> = {
                        exit_code,
                        arguments: moduleInput ? moduleInput.parameters : null,
                        app_uri: job.app_uri,
                        ended_at: job.ended_at,
                        job_uuid: job.uuid,
                        started_at: job.started_at,
                    };

                    let resultFolderName = 'job-results';
                    if ('main_result' in job && job.main_result !== null) {
                        resultFolderName = job.main_result.name;
                    } else if ('app_uri' in metadata) {
                        resultFolderName = `${metadata.app_uri}-results`;
                    }

                    const zip = new JSZip().folder(resultFolderName);
                    zip.file('.biolib/metadata.json', JSON.stringify(metadata, undefined, 4));
                    zip.file('.stdout.txt', stdout);
                    zip.file('.stderr.txt', stderr);

                    for (const file of outputFiles) {
                        const data = await file.getData();
                        // Removing '/' with substring so the path is relative to .folder(resultFolderName)
                        zip.file(file.path.substring(1), data);
                    }

                    if (moduleInput) {
                        const inputFilePaths = Object.keys(moduleInput.files);
                        if (inputFilePaths.length === 0) {
                            zip.file('biolib-input-files/.no-input-files-found', '');
                        } else {
                            for (const inputFilePath of inputFilePaths) {
                                const data = moduleInput.files[inputFilePath].data;
                                zip.file('biolib-input-files'.concat(inputFilePath), data);
                            }
                        }
                    }

                    const outputZipBlob = await zip.generateAsync({ type: 'blob' });
                    saveFileAs(`${resultFolderName}.zip`, 'zip', outputZipBlob);
                    return { data: null };
                } catch (error) {
                    console.error(error);
                    const message = `Failed to download and prepare result got error: ${error}`;
                    dispatch(notificationActions.addError(message));
                    return constructErrorPayload(message);
                }
            },
        }),
    }),
});

function constructErrorPayload(errorMessage: string) {
    // TODO: Consider what to do with status as it does not make much sense but required for typing
    return { error: { data: { message: errorMessage }, status: 200 } };
}

export { jobsApi };
export const {
    useCreateJobMutation,
    useDownloadJobResultMutation,
    useGetAccountJobMetricsQuery,
    useListJobsQuery,
    useStartJobMutation,
} = jobsApi;
